/**
 * COSMETIC UNLOCKS — the one place that decides whether a skin can be picked.
 *
 * Every skin family (pots: sim/potSkins.ts · greenhouse shell + floor: sim/houseSkins.ts · racks:
 * sim/shelfSkins.ts) has a default and a set of alternatives. The skins that shipped free stay free; the ones
 * marked `reward` are LOCKED until earned. Earning is DERIVED, never granted: an achievement lists the
 * cosmetics it unlocks (`AchievementReward.unlocks`, "kind:id"), and the set of held achievements says which
 * are open — so nothing is stored, nothing can be lost, and a player who already holds an achievement gets a
 * newly attached unlock the moment it ships.
 *
 * THE DEV REQUIREMENT OVERRIDE opens everything (`access.override`), exactly as it bypasses level and license
 * gates elsewhere. It never writes anything: turn it off and the locks come back.
 */
import { POT_SKINS, DEFAULT_POT_SKIN } from "./potSkins";
import { BUILDING_SKINS, FLOOR_SKINS } from "./houseSkins";
import { SHELF_SKINS } from "./shelfSkins";
import { FENCE_STYLES } from "./fenceStyles";

/** `frame` = an AVATAR BORDER (sim/avatar.ts AVATAR_FRAMES) — the picture-box skins on a profile. */
export type CosmeticKind = "pot" | "building" | "floor" | "shelf" | "decor" | "frame" | "fence";

export interface CosmeticAccess {
  /** the dev Requirement Override is on — every cosmetic opens */
  override?: boolean;
  /** "kind:id" keys unlocked by held achievements (achievements.ts cosmeticsFromAchievements) */
  earned?: ReadonlySet<string>;
}

export const cosmeticKey = (kind: CosmeticKind, id: string) => `${kind}:${id}`;

const REWARD = new Set<string>([
  ...BUILDING_SKINS.filter((s) => s.reward).map((s) => cosmeticKey("building", s.id)),
  ...FLOOR_SKINS.filter((s) => s.reward).map((s) => cosmeticKey("floor", s.id)),
  ...SHELF_SKINS.filter((s) => s.reward).map((s) => cosmeticKey("shelf", s.id)),
  // FENCE STYLES (sim/fenceStyles.ts): the reward styles lock the same way — "fence:<id>" from an achievement
  ...FENCE_STYLES.filter((s) => s.reward).map((s) => cosmeticKey("fence", s.id)),
]);

/** Can this skin be picked right now? House and shelf skins are open unless flagged `reward`; every pot skin
 *  other than terracotta has always been earned (see App's availableSkins), so the pot family asks the
 *  earned set for all of them. */
export function skinOpen(kind: CosmeticKind, id: string, access: CosmeticAccess = {}): boolean {
  if (access.override) return true;
  const key = cosmeticKey(kind, id);
  if (kind === "pot") return id === DEFAULT_POT_SKIN || !!access.earned?.has(key);
  return !REWARD.has(key) || !!access.earned?.has(key);
}

/** Is a REWARD DECOR item open for sale in the shop? Its achievement unlocks "decor:<id>" (and grants the first
 *  copy); the dev override opens every one. */
export function decorOpen(id: string, access: CosmeticAccess = {}): boolean {
  return !!access.override || !!access.earned?.has(cosmeticKey("decor", id));
}

/** Is this "kind:id" unlock held (or the dev override on)? The general form of `decorOpen`, for kinds whose
 *  catalogue lives outside this module (avatar borders: sim/avatar.ts `frameOpen`). */
export function cosmeticEarned(kind: CosmeticKind, id: string, access: CosmeticAccess = {}): boolean {
  return !!access.override || !!access.earned?.has(cosmeticKey(kind, id));
}

/** Every pot skin id — what the override opens in the pot picker. */
export const ALL_POT_SKIN_IDS: readonly string[] = POT_SKINS.map((s) => s.id);
